import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import * as echarts from "echarts";

function delay(ms) {
    return new Promise((r) => setTimeout(r, ms));
}

const FONDO_GRAFICA_WEB = "#191919";

/**
 * Captura un canvas ECharts tal como se ve en pantalla (tema oscuro, zoom aplicado).
 */
export async function capturarInstanciaEcharts(chart, opts = {}) {
    if (!chart || (typeof chart.isDisposed === "function" && chart.isDisposed())) return null;
    const {
        backgroundColor = FONDO_GRAFICA_WEB,
        pixelRatio = 2,
        excludeComponents = ["dataZoom", "toolbox"],
    } = opts;
    await delay(320);
    try {
        return chart.getDataURL({
            type: "png",
            pixelRatio,
            backgroundColor,
            excludeComponents,
        });
    } catch {
        return null;
    }
}

/**
 * Option lista para PDF: sin dataZoom (serie completa visible) y más área de trazado.
 */
export function opcionGraficaExportPdf(option) {
    if (!option || typeof option !== "object") return null;
    const { dataZoom: _dz, ...rest } = option;
    const grid = { ...(rest.grid || {}), bottom: 44 };
    const series = Array.isArray(rest.series)
        ? rest.series.map((s) => ({ ...s, connectNulls: true }))
        : rest.series;
    return { ...rest, grid, series };
}

/** Renderiza off-screen la misma option + tema que la vista y devuelve PNG. */
export async function capturarOpcionEcharts(option, theme = "dark2", size = { width: 1000, height: 560 }) {
    const optPdf = opcionGraficaExportPdf(option);
    if (!optPdf) return null;
    const div = document.createElement("div");
    div.style.cssText = `width:${size.width}px;height:${size.height}px;position:fixed;left:-12000px;top:0;visibility:hidden;pointer-events:none`;
    document.body.appendChild(div);
    const ch = echarts.init(div, theme, { renderer: "canvas" });
    ch.setOption(optPdf, true);
    ch.resize({ width: size.width, height: size.height });
    const url = await capturarInstanciaEcharts(ch, { excludeComponents: ["toolbox"] });
    ch.dispose();
    document.body.removeChild(div);
    return url;
}

function fasesParaMarkAreaPdf(prueba, newfases, inicioFases) {
    const lista = (Array.isArray(newfases) && newfases.length > 0 ? newfases : prueba?.fases) || [];
    const off = Array.isArray(newfases) && newfases.length > 0 ? inicioFases : prueba?.inicioFases ?? 0;
    const n = prueba?.sistema_de_pesos_y_tiempos?.length ?? 0;
    const maxI = n > 0 ? n - 1 : 0;
    return lista
        .filter((f) => f && typeof f.fin === "number" && typeof f.inicio === "number" && f.fin >= f.inicio)
        .map((f) => {
            let lo = Math.round(f.inicio + off);
            let hi = Math.round(f.fin + off);
            if (n > 0) {
                lo = Math.max(0, Math.min(maxI, lo));
                hi = Math.max(0, Math.min(maxI, hi));
                if (lo > hi) [lo, hi] = [hi, lo];
            }
            return [
                { name: f.nombre, xAxis: lo, itemStyle: { color: f.color, opacity: 0.45 } },
                { xAxis: hi },
            ];
        });
}

/**
 * Renderiza la misma serie de la vista en un ECharts off-screen (fondo claro, leyenda visible) y devuelve data URL PNG.
 */
export async function capturarGraficaPdfExport(prueba, newfases, inicioFases, tipoPrueba) {
    const datos = prueba?.sistema_de_pesos_y_tiempos ?? [];
    if (!datos.length) return null;

    const div = document.createElement("div");
    div.style.cssText =
        "width:1000px;height:560px;position:fixed;left:-12000px;top:0;visibility:hidden;pointer-events:none";
    document.body.appendChild(div);
    const ch = echarts.init(div, null, { renderer: "canvas" });

    const mark = fasesParaMarkAreaPdf(prueba, newfases, inicioFases);
    const tiempos = datos.map((s) => Number(s?.tiempo ?? 0).toFixed(1));
    const titulo = `${prueba?.tipoPrueba ?? "Prueba"} — fuerza vs tiempo`;

    let option;
    if (tipoPrueba === "Dynamo") {
        option = {
            backgroundColor: "#ffffff",
            animation: false,
            title: { text: titulo, left: "center", top: 4, textStyle: { color: "#111", fontSize: 14 } },
            legend: { show: true, top: 30, data: ["Valor"], textStyle: { color: "#222" } },
            grid: { left: 58, right: 28, top: 58, bottom: 52, containLabel: true },
            xAxis: {
                type: "category",
                name: "Tiempo (s)",
                nameLocation: "middle",
                nameGap: 30,
                data: tiempos,
                axisLabel: { color: "#333", rotate: 30 },
                nameTextStyle: { color: "#333" },
            },
            yAxis: {
                type: "value",
                name: "Fuerza (N)",
                nameLocation: "middle",
                nameGap: 44,
                axisLabel: { color: "#333" },
                nameTextStyle: { color: "#333" },
            },
            series: [
                {
                    name: "Valor",
                    type: "line",
                    symbol: "none",
                    data: datos.map((d) => d?.peso?.combinada ?? 0),
                    lineStyle: { width: 1.6, color: "#d48806" },
                    markArea: mark.length
                        ? { silent: true, itemStyle: { opacity: 0.35 }, label: { color: "#555" }, data: mark }
                        : undefined,
                },
            ],
        };
    } else {
        const esCOP = tipoPrueba === "COP";
        const etiquetas = esCOP ? ["P1", "P2", "Combinada"] : ["Píe izquierdo", "Píe derecho", "Combinado"];
        option = {
            backgroundColor: "#ffffff",
            animation: false,
            title: { text: titulo, left: "center", top: 4, textStyle: { color: "#111", fontSize: 14 } },
            legend: {
                show: true,
                top: 30,
                type: "scroll",
                data: etiquetas,
                textStyle: { color: "#222" },
            },
            grid: { left: 58, right: 28, top: 74, bottom: 58, containLabel: true },
            xAxis: {
                type: "category",
                name: "Tiempo (s)",
                nameLocation: "middle",
                nameGap: 30,
                data: tiempos,
                axisLabel: { color: "#333", rotate: 30 },
                nameTextStyle: { color: "#333" },
            },
            yAxis: {
                type: "value",
                name: "Fuerza (N)",
                nameLocation: "middle",
                nameGap: 48,
                axisLabel: { color: "#333" },
                nameTextStyle: { color: "#333" },
            },
            series: [
                {
                    name: etiquetas[0],
                    type: "line",
                    symbol: "none",
                    data: datos.map((s) => s?.peso?.p1 ?? s?.peso?.izq ?? 0),
                    lineStyle: { width: 1.2 },
                },
                {
                    name: etiquetas[1],
                    type: "line",
                    symbol: "none",
                    data: datos.map((s) => s?.peso?.p2 ?? s?.peso?.der ?? 0),
                    lineStyle: { width: 1.2 },
                },
                {
                    name: etiquetas[2],
                    type: "line",
                    symbol: "none",
                    smooth: true,
                    data: datos.map((d) => d?.peso?.combinada ?? 0),
                    lineStyle: { width: 1.6, color: "#d48806" },
                    markArea: mark.length
                        ? {
                              silent: true,
                              itemStyle: { opacity: 0.35 },
                              label: { color: "#555", rotate: 28 },
                              data: mark,
                          }
                        : undefined,
                },
            ],
        };
    }

    ch.setOption(option, true);
    await delay(480);
    const url = ch.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: "#ffffff" });
    ch.dispose();
    document.body.removeChild(div);
    return url;
}

function formatoCeldaValor(v, u) {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    const core = Number.isFinite(n) ? n.toFixed(2) : String(v);
    return u ? `${core} ${u}`.trim() : core;
}

/**
 * @param {Array<{ nombre?: string, valor?: unknown, u?: string, media?: unknown, porcentaje?: unknown, dif?: unknown }>} parametros
 * @param {boolean} conMuestra - si hay columnas extra de comparación con muestra
 */
export function parametrosATablaFilas(parametros, conMuestra) {
    if (!Array.isArray(parametros)) return { head: [], body: [] };
    if (conMuestra) {
        return {
            head: [["Parámetro", "Valor", "Muestra", "% dif.", "Dif."]],
            body: parametros.map((p) => [
                String(p.nombre ?? ""),
                formatoCeldaValor(p.valor, p.u),
                p.media !== undefined && p.media !== "" ? formatoCeldaValor(p.media, p.u) : "",
                p.porcentaje !== undefined && Number.isFinite(Number(p.porcentaje))
                    ? `${Number(p.porcentaje).toFixed(2)}%`
                    : "",
                p.dif !== undefined && p.dif !== "" ? formatoCeldaValor(p.dif, p.u) : "",
            ]),
        };
    }
    return {
        head: [["Parámetro", "Valor"]],
        body: parametros.map((p) => [String(p.nombre ?? ""), formatoCeldaValor(p.valor, p.u)]),
    };
}

function asegurarDataUrlPng(src) {
    if (!src || typeof src !== "string") return null;
    if (src.startsWith("data:")) return src;
    return `data:image/png;base64,${src}`;
}

/**
 * Genera y descarga el PDF del resumen (gráfica + fases + tabla completa de parámetros + notas).
 * @param {object} opts
 * @param {string} opts.imagenPrincipalDataUrl - PNG base64 (puede incluir prefijo data:)
 * @param {Array<{ dataUrl: string, titulo: string }>} [opts.imagenesExtra]
 * @param {Array<{ nombre?: string, tiempo?: { duracion?: number } }>} [opts.fases]
 * @param {{ head: string[][], body: string[][] }} opts.tablaParametros - resultado de parametrosATablaFilas
 * @param {object} opts.atleta
 * @param {object} opts.prueba
 * @param {string} [opts.nombreArchivo]
 */
export function descargarResumenPdf(opts) {
    const {
        imagenPrincipalDataUrl,
        imagenesExtra = [],
        fases = [],
        tablaParametros,
        atleta,
        prueba,
        nombreArchivo,
    } = opts;

    const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const m = 12;
    let y = 14;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("SEFUM — Resumen de prueba", pageW / 2, y, { align: "center" });
    y += 9;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Atleta: ${atleta?.nombre ?? ""}`, m, y);
    const fechaPrueba = prueba?.prueba?.createdAt || prueba?.createdAt;
    doc.text(`Fecha: ${fechaPrueba ? dayjs(fechaPrueba).format("DD/MM/YYYY HH:mm") : ""}`, m + 78, y);
    y += 5;
    doc.text(`Prueba: ${prueba?.tipoPrueba ?? ""}  ·  Momento: ${prueba?.momento ?? ""}`, m, y);
    if (atleta?.peso != null) {
        doc.text(`Peso: ${atleta.peso} kg`, m + 120, y);
    }
    y += 8;

    const img1 = asegurarDataUrlPng(imagenPrincipalDataUrl);
    if (img1) {
        const maxW = pageW - 2 * m;
        const props = doc.getImageProperties(img1);
        const imgH = (maxW * props.height) / props.width;
        const maxH = 95;
        const h = Math.min(imgH, maxH);
        const w = (h * props.width) / props.height;
        if (y + h > pageH - m) {
            doc.addPage();
            y = m;
        }
        doc.addImage(img1, "PNG", m, y, w, h);
        y += h + 6;
    }

    for (const extra of imagenesExtra) {
        const url = asegurarDataUrlPng(extra?.dataUrl);
        if (!url) continue;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        if (y + 18 > pageH - m) {
            doc.addPage();
            y = m;
        }
        doc.text(String(extra.titulo || "Gráfica"), m, y);
        y += 5;
        doc.setFont("helvetica", "normal");
        const maxW = pageW - 2 * m;
        const props = doc.getImageProperties(url);
        const imgH = (maxW * props.height) / props.width;
        const maxH = 105;
        const h = Math.min(imgH, maxH);
        const w = (h * props.width) / props.height;
        if (y + h > pageH - m) {
            doc.addPage();
            y = m;
        }
        doc.addImage(url, "PNG", m, y, w, h);
        y += h + 8;
    }

    const fasesList = Array.isArray(fases) ? fases.filter((f) => f && f.nombre) : [];
    if (fasesList.length) {
        if (y + 14 > pageH - m) {
            doc.addPage();
            y = m;
        }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Fases", m, y);
        y += 6;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        for (const f of fasesList) {
            const dur = f?.tiempo?.duracion;
            const durTxt = dur != null && Number.isFinite(Number(dur)) ? `${Number(dur).toFixed(3)} s` : "—";
            const line = `${f.nombre}: ${durTxt}`;
            if (y + 4 > pageH - m) {
                doc.addPage();
                y = m;
            }
            doc.text(line, m, y);
            y += 4.2;
        }
        y += 4;
    }

    const { head, body } = tablaParametros || { head: [["Parámetro", "Valor"]], body: [] };
    if (body.length) {
        if (y + 24 > pageH - m) {
            doc.addPage();
            y = m;
        }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Parámetros", m, y);
        y += 4;
        autoTable(doc, {
            startY: y,
            head,
            body,
            styles: { fontSize: 7, cellPadding: 1.2, overflow: "linebreak", valign: "top" },
            headStyles: { fillColor: [60, 60, 60], textColor: 255, fontStyle: "bold" },
            margin: { left: m, right: m },
            tableWidth: "auto",
            showHead: "everyPage",
            pageBreak: "auto",
            rowPageBreak: "avoid",
            theme: "striped",
        });
        y = doc.lastAutoTable.finalY + 8;
    }

    const notas = prueba?.impresion;
    if (notas) {
        if (y + 20 > pageH - m) {
            doc.addPage();
            y = m;
        }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("Impresiones / notas", m, y);
        y += 5;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        const split = doc.splitTextToSize(String(notas), pageW - 2 * m);
        for (const line of split) {
            if (y + 4 > pageH - m) {
                doc.addPage();
                y = m;
            }
            doc.text(line, m, y);
            y += 4;
        }
    }

    const nombre =
        nombreArchivo ||
        `Resumen_${(atleta?.nombre || "atleta").replace(/\s+/g, "_")}_${prueba?.tipoPrueba || "prueba"}_${dayjs(prueba?.prueba?.createdAt || prueba?.createdAt).format("YYYY-MM-DD")}.pdf`;
    doc.save(nombre);
}
